import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, TextField, Typography } from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import DeleteRounded from "@mui/icons-material/DeleteRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import SaveRounded from "@mui/icons-material/SaveRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import AgenteContabilidadeShell from "../components/AgenteContabilidadeShell";
import { getDreSimulationTitle } from "../components/DreSimulationHistoryPanel";
import DreTable from "../components/DreTable";
import EmptyState from "../components/EmptyState";
import FilterSelect from "../components/FilterSelect";
import KpiGrid from "../components/KpiGrid";
import MonthFallbackNotice from "../components/MonthFallbackNotice";
import PeriodSelector from "../components/PeriodSelector";
import { colors } from "../constants/designTokens";
import { useAgenteContabilidadeStore } from "../hooks/useAgenteContabilidadeStore";
import { usePageEmpresaSelection } from "../hooks/usePageEmpresaSelection";
import { usePageMonthSelection } from "../hooks/usePageMonthSelection";
import {
  calcFullDre,
  getEmptyLineData,
} from "../services/dreService";
import {
  atualizarDreViabilidadeVersao,
  criarDreViabilidadeVersao,
  excluirDreViabilidadeVersao,
  getContabilidadeApiErrorMessage,
  logContabilidadeApiError,
  listarDreViabilidadeVersoes,
  obterDreViabilidade,
  obterDreViabilidadeVersao,
} from "../services/agenteContabilidadeApiService";
import { formatCurrency, formatPercent } from "../utils/formatters";
import { resolveActiveMonth } from "../utils/intersecaoEmpreendimentos";
import {
  getEmpreendimentoSelectionKey,
  normalizeEmpreendimentoContext,
} from "../utils/empreendimentoSelector";
import {
  clearSelectedDreReference,
  clearSelectedDreSimulation,
  readSelectedDreReference,
  readSelectedDreSimulation,
  resolveDreSimulationForMonth,
  writeSelectedDreReference,
  writeSelectedDreSimulation,
} from "../utils/dreSimulationSession";

const getFirstValue = (item, keys, fallback = 0) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) {
      return item[key];
    }
  }

  return fallback;
};

const toNumber = (value) => {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

function getEmpreendimentoOptionValue(item) {
  return String(
    getFirstValue(item, [
      "idClienteEmpresa",
      "id_cliente_empresa",
      "id",
    ], "")
  ).trim();
}

function getEmpreendimentoOptionLabel(item, fallbackId) {
  return (
    getFirstValue(item, [
      "nomeEmpresa",
      "nome_empresa",
      "empresaArquivo",
      "empresa_arquivo",
      "nomeObra",
      "nome_obra",
      "obraArquivo",
      "obra_arquivo",
      "codigoObraErp",
      "codigo_obra_erp",
    ], "") || `Empreendimento ${fallbackId}`
  );
}

function normalizeLoadedLine(line) {
  return {
    apropriado: toNumber(getFirstValue(line, ["apropriado"])),
    a_apropriar: toNumber(getFirstValue(line, ["a_apropriar", "aApropriar", "AApropriar", "aapropriar"])),
    a_contratar: toNumber(getFirstValue(line, ["a_contratar", "aContratar", "AContratar", "acontratar"])),
  };
}

function normalizeDreItem(item) {
  const dadosCarregados = item?.dadosCarregados || item?.dados_carregados || {};
  const normalizedDados = {};

  Object.entries(dadosCarregados).forEach(([key, value]) => {
    normalizedDados[key] = normalizeLoadedLine(value);
  });

  return {
    mes_ano: getFirstValue(item, ["mes_ano", "mesAno", "competencia", "mes"], ""),
    dadosCarregados: normalizedDados,
  };
}

function normalizeDreResponse(payload) {
  if (!payload) {
    return null;
  }

  const sourceTimeline =
    (Array.isArray(payload) && payload) ||
    payload.timeline ||
    payload.grafico ||
    payload.registros ||
    payload.items ||
    payload.data ||
    [];

  if (!Array.isArray(sourceTimeline)) {
    return null;
  }

  const timeline = sourceTimeline.map(normalizeDreItem).filter((item) => item.mes_ano);
  const months = Array.isArray(payload.meses)
    ? payload.meses
    : Array.isArray(payload.months)
    ? payload.months
    : timeline.map((item) => item.mes_ano);

  return {
    months,
    timeline,
  };
}

function linhasToLineData(linhas = {}, colunas = {}) {
  const base = getEmptyLineData();
  Object.entries(linhas || {}).forEach(([key, value]) => {
    if (base[key]) {
      base[key] = { ...base[key], viabilidade: toNumber(value) };
    }
  });
  Object.entries(colunas || {}).forEach(([column, values]) => {
    Object.entries(values || {}).forEach(([key, value]) => {
      if (base[key]) {
        base[key] = { ...base[key], [column]: toNumber(value) };
      }
    });
  });
  return base;
}

function lineDataToLinhas(lineData = {}) {
  return Object.entries(lineData).reduce((acc, [key, value]) => {
    acc[key] = toNumber(value?.viabilidade);
    return acc;
  }, {});
}

function lineDataToColunas(lineData = {}) {
  return ["apropriado", "a_apropriar", "a_contratar"].reduce((columns, column) => {
    const values = Object.entries(lineData).reduce((result, [key, value]) => {
      if (Object.prototype.hasOwnProperty.call(value || {}, column)) {
        result[key] = toNumber(value[column]);
      }
      return result;
    }, {});
    if (Object.keys(values).length) {
      columns[column] = values;
    }
    return columns;
  }, {});
}

function buildVersionSignature({ nomeVersao = "", descricao = "", mesAnoBase = "", lineData = {} }) {
  const values = Object.keys(lineData || {})
    .sort()
    .reduce((acc, key) => {
      acc[key] = Object.keys(lineData[key] || {})
        .sort()
        .reduce((line, column) => {
          line[column] = toNumber(lineData[key]?.[column]);
          return line;
        }, {});
      return acc;
    }, {});

  return JSON.stringify({
    nomeVersao: nomeVersao.trim(),
    descricao: descricao.trim(),
    mesAnoBase: mesAnoBase || "",
    values,
  });
}

function buildSelectedSimulation(version, lineData, calculatedRows) {
  return {
    id: version?.id,
    nomeVersao: version?.nomeVersao || "",
    descricao: version?.descricao || "",
    mesAnoBase: version?.mesAnoBase || "",
    linhas: lineDataToLinhas(lineData),
    colunas: lineDataToColunas(lineData),
    indicadores: {
      margemViabilidade: toNumber(calculatedRows?.lucro_liquido?.pct_viab),
      margemTendencia: toNumber(calculatedRows?.lucro_liquido?.pct_tend),
    },
  };
}

function getVersionTitle(version) {
  if (!version) return "";
  const month = version.mesAnoBase || version.mes_ano_base;
  const name = version.nomeVersao || version.nome_versao || "Simulação";
  return month ? `${name} - ${month}` : name;
}

export default function DreViabilidadePage() {
  const { tenant, state, actions } = useAgenteContabilidadeStore();
  const [selectedMonth, setSelectedMonth] = usePageMonthSelection("dre-viabilidade");
  const [apiAvailableMonths, setApiAvailableMonths] = useState([]);
  const [apiDreData, setApiDreData] = useState(null);
  const [apiStatus, setApiStatus] = useState("idle");
  const [lineData, setLineData] = useState(getEmptyLineData());
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [versionName, setVersionName] = useState("");
  const [versionDescription, setVersionDescription] = useState("");
  const [versionsStatus, setVersionsStatus] = useState("idle");
  const [savingVersion, setSavingVersion] = useState(false);
  const [versionMessage, setVersionMessage] = useState(null);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [loadedVersionSignature, setLoadedVersionSignature] = useState("");
  const restoredSelectionKeyRef = useRef("");

  const backendEmpreendimentos = useMemo(
    () => state.backendCatalog?.empreendimentos || [],
    [state.backendCatalog?.empreendimentos]
  );
  const backendCatalogSyncKey = state.backendCatalog?.lastSyncAt || "";
  const hasBackendEmpreendimentos = backendEmpreendimentos.length > 0;
  const backendEmpreendimentoOptions = useMemo(() => {
    const optionsByValue = new Map();

    backendEmpreendimentos.forEach((item) => {
      const value = getEmpreendimentoOptionValue(item);
      if (!value || optionsByValue.has(value)) {
        return;
      }

      optionsByValue.set(value, {
        value,
        label: getEmpreendimentoOptionLabel(item, value),
        selectionKey: getEmpreendimentoSelectionKey(item),
        context: normalizeEmpreendimentoContext(item),
      });
    });

    return Array.from(optionsByValue.values());
  }, [backendEmpreendimentos]);
  const [selectedBackendEmpreendimento, setSelectedBackendEmpreendimento] = usePageEmpresaSelection(
    "dre-viabilidade",
    backendEmpreendimentoOptions
  );
  const selectedBackendEmpresaItem = useMemo(
    () => backendEmpreendimentos.find(
      (item) => getEmpreendimentoOptionValue(item) === String(selectedBackendEmpreendimento || "")
    ),
    [backendEmpreendimentos, selectedBackendEmpreendimento]
  );
  const selectedEmpreendimentoContext = useMemo(
    () => normalizeEmpreendimentoContext(selectedBackendEmpresaItem),
    [selectedBackendEmpresaItem]
  );
  const selectedBackendEmpresaId = selectedBackendEmpreendimento;
  const selectedBackendObraId = String(
    getFirstValue(selectedBackendEmpresaItem, [
      "idClienteObra",
      "id_cliente_obra",
      "idObra",
      "id_obra",
    ], "")
  ).trim();

  useEffect(() => {
    actions.refreshBackendEmpreendimentos().catch(() => {});
  }, [actions]);

  useEffect(() => {
    let active = true;

    async function fetchDreViabilidade() {
      if (!backendCatalogSyncKey && !selectedBackendEmpreendimento) {
        setApiStatus("loading");
        return;
      }

      if (hasBackendEmpreendimentos && !selectedBackendEmpreendimento) {
        setApiStatus("loading");
        return;
      }

      setApiStatus("loading");

      try {
        const params = {
          ...(selectedBackendEmpresaId ? { idClienteEmpresa: selectedBackendEmpresaId } : {}),
        };
        const payload = await obterDreViabilidade(params);
        const normalizedData = normalizeDreResponse(payload);

        if (!active) return;

        if (normalizedData?.timeline?.length) {
          setApiAvailableMonths(normalizedData.months);
          setApiDreData(normalizedData);
          setApiStatus("success");
        } else {
          setApiDreData(null);
          setApiStatus("empty");
        }
      } catch (error) {
        if (!active) return;
        logContabilidadeApiError("dre-viabilidade", error);
        setApiStatus("error");
      }
    }

    fetchDreViabilidade();

    return () => {
      active = false;
    };
  }, [backendCatalogSyncKey, hasBackendEmpreendimentos, selectedBackendEmpresaId, selectedBackendEmpreendimento]);

  useEffect(() => {
    setLineData(getEmptyLineData());
    setSelectedVersionId("");
    setVersionName("");
    setVersionDescription("");
    setVersionMessage(null);
    setSaveAttempted(false);
    setLoadedVersionSignature("");
    restoredSelectionKeyRef.current = "";
  }, [selectedBackendEmpreendimento]);

  useEffect(() => {
    let active = true;

    async function fetchVersions() {
      if (!selectedBackendEmpreendimento) {
        setVersions([]);
        setVersionsStatus("idle");
        return;
      }

      setVersionsStatus("loading");
      try {
        const payload = await listarDreViabilidadeVersoes({
          idClienteEmpresa: selectedBackendEmpresaId,
        });
        if (!active) return;
        setVersions(Array.isArray(payload) ? payload : []);
        setVersionsStatus("success");
      } catch (error) {
        if (!active) return;
        logContabilidadeApiError("dre-viabilidade-versoes", error);
        setVersions([]);
        setVersionsStatus("error");
      }
    }

    fetchVersions();

    return () => {
      active = false;
    };
  }, [selectedBackendEmpresaId, selectedBackendEmpreendimento]);

  const usingApiData = Boolean(apiDreData?.timeline?.length);
  const months = usingApiData && apiAvailableMonths.length
    ? apiAvailableMonths
    : apiDreData?.months || [];

  const activeMonth = resolveActiveMonth(months, selectedMonth);

  const apiSnapshot = apiDreData?.timeline?.find((item) => item.mes_ano === activeMonth) || apiDreData?.timeline?.[apiDreData.timeline.length - 1];
  const dadosCarregados = useMemo(() => {
    if (usingApiData) {
      return apiSnapshot?.dadosCarregados || {};
    }

    return {};
  }, [apiSnapshot, usingApiData]);

  useEffect(() => {
    const restoreKey = `${tenant.clientId || "anon"}|${selectedBackendEmpresaId}|${activeMonth}`;
    if (
      !selectedBackendEmpresaId ||
      !activeMonth ||
      restoredSelectionKeyRef.current === restoreKey
    ) {
      return;
    }
    restoredSelectionKeyRef.current = restoreKey;

    const reference = readSelectedDreReference(
      tenant.clientId,
      selectedBackendEmpresaId
    );
    const monthlySimulation = readSelectedDreSimulation(
      tenant.clientId,
      selectedBackendEmpresaId,
      activeMonth
    );
    const stored = resolveDreSimulationForMonth(reference, monthlySimulation);
    if (!stored?.id) {
      return;
    }

    const restoredLineData = linhasToLineData(stored.linhas, stored.colunas);
    setSelectedVersionId(String(stored.id));
    setVersionName(stored.nomeVersao || "");
    setVersionDescription(stored.descricao || "");
    setLineData(restoredLineData);
    setLoadedVersionSignature(
      buildVersionSignature({
        nomeVersao: stored.nomeVersao || "",
        descricao: stored.descricao || "",
        mesAnoBase: stored.mesAnoBase || activeMonth,
        lineData: restoredLineData,
      })
    );
  }, [activeMonth, selectedBackendEmpresaId, tenant.clientId]);

  const calculatedRows = useMemo(
    () => calcFullDre(lineData, null, dadosCarregados),
    [dadosCarregados, lineData]
  );
  const impostoPercentual = calculatedRows.impostos?.pct_viab || 0;

  const summaryMetrics = [
    { label: "VGV Bruto", value: formatCurrency(calculatedRows.vgv?.tendencia || 0), variant: "green", icon: BusinessRounded },
    { label: "VGV Liquido", value: formatCurrency(calculatedRows.vgv_liquido?.tendencia || 0), variant: "blue", icon: DescriptionOutlined },
    { label: "Resultado Bruto", value: formatCurrency(calculatedRows.resultado_bruto?.tendencia || 0), variant: "purple", icon: SaveRounded },
    { label: "Lucro Operacional", value: formatCurrency(calculatedRows.lucro_operacional?.tendencia || 0), variant: "orange", icon: EditRounded },
    { label: "Lucro Liquido", value: formatCurrency(calculatedRows.lucro_liquido?.tendencia || 0), variant: "magenta", icon: DescriptionOutlined },
    {
      label: "Margem Líquida na Viabilidade",
      value: selectedVersionId
        ? formatPercent(calculatedRows.lucro_liquido?.pct_viab, { decimals: 2 })
        : "Sem simulação",
      variant: "red",
      icon: BusinessRounded,
    },
  ];

  const empreendimentoOptions = backendEmpreendimentoOptions;
  const selectedEmpreendimentoValue = selectedBackendEmpreendimento;
  const hasVersionHistory = versions.length > 0;
  const hasViabilidadeValue = useMemo(
    () => Object.values(lineData).some((item) => toNumber(item?.viabilidade) >= 1),
    [lineData]
  );
  const hasNegativeDreValue = useMemo(
    () => Object.values(lineData).some((item) =>
      Object.values(item || {}).some((value) => toNumber(value) < 0)
    ),
    [lineData]
  );
  const currentVersionSignature = useMemo(
    () =>
      buildVersionSignature({
        nomeVersao: versionName,
        descricao: versionDescription,
        mesAnoBase: activeMonth,
        lineData,
      }),
    [activeMonth, lineData, versionDescription, versionName]
  );
  const hasPendingChanges = selectedVersionId
    ? currentVersionSignature !== loadedVersionSignature
    : Boolean(versionName.trim() || versionDescription.trim() || hasViabilidadeValue);
  const canSaveVersion = Boolean(
    selectedBackendEmpreendimento &&
      versionName.trim() &&
      hasViabilidadeValue &&
      !hasNegativeDreValue &&
      hasPendingChanges &&
      !savingVersion
  );
  const selectedVersion = versions.find((version) => String(version.id) === String(selectedVersionId));
  const selectedVersionTitle = selectedVersion ? getDreSimulationTitle(selectedVersion) : "";
  const versionStatusLabel = selectedVersionId
    ? hasPendingChanges
    ? "Alterações pendentes"
      : "Salva"
    : "Nova simulação";
  const versionStatusColor = selectedVersionId
    ? hasPendingChanges
      ? { bgcolor: "#fff7d6", color: "#8a5a00" }
      : { bgcolor: "#d8f9e9", color: "#08765c" }
    : { bgcolor: "#eef4ff", color: colors.primary };
  const saveHelperText = !selectedBackendEmpreendimento
    ? "Selecione uma empresa para salvar a simulação."
    : !versionName.trim()
      ? "Informe um nome para identificar esta simulação."
      : hasNegativeDreValue
        ? "Valores negativos não são permitidos na DRE."
      : !hasViabilidadeValue
        ? "Preencha pelo menos um valor maior ou igual a R$ 1,00 na coluna Viabilidade."
        : !hasPendingChanges
          ? "Nenhuma alteração encontrada para salvar."
          : "Alterações prontas para salvar.";

  useEffect(() => {
    if (
      !selectedVersionId ||
      !activeMonth ||
      currentVersionSignature !== loadedVersionSignature
    ) {
      return;
    }

    const selectedSimulation = buildSelectedSimulation(
      {
        ...selectedVersion,
        id: selectedVersionId,
        nomeVersao: versionName,
        descricao: versionDescription,
        mesAnoBase: activeMonth,
      },
      lineData,
      calculatedRows
    );

    writeSelectedDreSimulation(
      tenant.clientId,
      selectedBackendEmpresaId,
      activeMonth,
      selectedSimulation
    );
    writeSelectedDreReference(
      tenant.clientId,
      selectedBackendEmpresaId,
      selectedSimulation
    );
  }, [
    activeMonth,
    calculatedRows,
    currentVersionSignature,
    lineData,
    loadedVersionSignature,
    selectedBackendEmpresaId,
    selectedVersion,
    selectedVersionId,
    tenant.clientId,
    versionDescription,
    versionName,
  ]);

  function handleEmpreendimentoChange(value) {
    setSelectedMonth((current) => current || activeMonth);
    setSelectedBackendEmpreendimento(value || "");
    setApiAvailableMonths([]);
  }

  function handleLineChange(key, column, value) {
    setLineData((current) => ({
      ...current,
      [key]: {
        ...current[key],
        [column]: value,
      },
    }));
  }

  async function handleVersionChange(value) {
    setSelectedVersionId(value || "");
    setVersionMessage(null);

    if (!value) {
      setLineData(getEmptyLineData());
      setVersionName("");
      setVersionDescription("");
      setLoadedVersionSignature("");
      setSaveAttempted(false);
      clearSelectedDreReference(
        tenant.clientId,
        selectedBackendEmpresaId
      );
      return;
    }

    try {
      const version = await obterDreViabilidadeVersao(value);
      const loadedName = version.nomeVersao || "";
      const loadedDescription = version.descricao || "";
      const loadedLineData = linhasToLineData(version.linhas, version.colunas);
      const loadedMonth = version.mesAnoBase || activeMonth;
      const loadedRows = calcFullDre(loadedLineData, null, dadosCarregados);
      setVersionName(loadedName);
      setVersionDescription(loadedDescription);
      setLineData(loadedLineData);
      setLoadedVersionSignature(
        buildVersionSignature({
          nomeVersao: loadedName,
          descricao: loadedDescription,
          mesAnoBase: loadedMonth,
          lineData: loadedLineData,
        })
      );
      setSaveAttempted(false);
      if (loadedMonth) {
        setSelectedMonth(loadedMonth);
        const selectedSimulation = buildSelectedSimulation(version, loadedLineData, loadedRows);
        writeSelectedDreSimulation(
          tenant.clientId,
          selectedBackendEmpresaId,
          loadedMonth,
          selectedSimulation
        );
        writeSelectedDreReference(
          tenant.clientId,
          selectedBackendEmpresaId,
          selectedSimulation
        );
      }
    } catch (error) {
      logContabilidadeApiError("obter-versao-dre", error);
      setVersionMessage({ severity: "error", text: "Não foi possível carregar a versão selecionada." });
    }
  }

  function handleNewVersion() {
    setSelectedVersionId("");
    setVersionName("");
    setVersionDescription("");
    setLineData(getEmptyLineData());
    setVersionMessage(null);
    setSaveAttempted(false);
    setLoadedVersionSignature("");
    clearSelectedDreSimulation(
      tenant.clientId,
      selectedBackendEmpresaId,
      activeMonth
    );
    clearSelectedDreReference(
      tenant.clientId,
      selectedBackendEmpresaId
    );
  }

  async function refreshVersions(nextSelectedId) {
    if (!selectedBackendEmpreendimento) return;
    const payload = await listarDreViabilidadeVersoes({
      idClienteEmpresa: selectedBackendEmpresaId,
    });
    setVersions(Array.isArray(payload) ? payload : []);
    if (nextSelectedId !== undefined) {
      setSelectedVersionId(nextSelectedId ? String(nextSelectedId) : "");
    }
  }

  async function handleSaveVersion() {
    setSaveAttempted(true);

    if (!selectedBackendEmpreendimento) {
      setVersionMessage({ severity: "warning", text: "Selecione uma empresa para salvar a simulação." });
      return;
    }

    if (!versionName.trim()) {
      setVersionMessage({ severity: "warning", text: "Informe um nome para identificar esta simulação." });
      return;
    }

    if (hasNegativeDreValue) {
      setVersionMessage({ severity: "warning", text: "Valores negativos não são permitidos na DRE." });
      return;
    }

    if (!hasViabilidadeValue) {
      setVersionMessage({ severity: "warning", text: "Preencha pelo menos um valor maior ou igual a R$ 1,00 na coluna Viabilidade." });
      return;
    }

    if (!hasPendingChanges) {
      setVersionMessage({ severity: "info", text: "Nenhuma alteração encontrada para salvar. Altere algum campo da simulação antes de salvar novamente." });
      return;
    }

    if (!selectedBackendObraId) {
      setVersionMessage({ severity: "warning", text: "Não foi possível identificar a obra vinculada a esta empresa para salvar a simulação." });
      return;
    }

    setSavingVersion(true);
    setVersionMessage(null);

    const payload = {
      idClienteObra: Number(selectedBackendObraId),
      nomeVersao: versionName.trim(),
      descricao: versionDescription.trim() || null,
      mesAnoBase: activeMonth || null,
      linhas: lineDataToLinhas(lineData),
      colunas: lineDataToColunas(lineData),
    };

    try {
      const saved = selectedVersionId
        ? await atualizarDreViabilidadeVersao(selectedVersionId, payload)
        : await criarDreViabilidadeVersao(payload);
      const savedName = saved.nomeVersao || payload.nomeVersao;
      const savedDescription = saved.descricao || "";
      const savedMonth = saved.mesAnoBase || payload.mesAnoBase || activeMonth;
      setVersionName(savedName);
      setVersionDescription(savedDescription);
      setLoadedVersionSignature(
        buildVersionSignature({
          nomeVersao: savedName,
          descricao: savedDescription,
          mesAnoBase: savedMonth,
          lineData,
        })
      );
      setSaveAttempted(false);
      await refreshVersions(saved.id);
      const selectedSimulation = buildSelectedSimulation(
        { ...saved, nomeVersao: savedName, mesAnoBase: savedMonth },
        lineData,
        calculatedRows
      );
      writeSelectedDreSimulation(
        tenant.clientId,
        selectedBackendEmpresaId,
        savedMonth,
        selectedSimulation
      );
      writeSelectedDreReference(
        tenant.clientId,
        selectedBackendEmpresaId,
        selectedSimulation
      );
      setVersionMessage({ severity: "success", text: "Simulação salva com sucesso." });
    } catch (error) {
      logContabilidadeApiError("salvar-versao-dre", error);
      setVersionMessage({
        severity: "error",
        text: getContabilidadeApiErrorMessage(
          error,
          "Não foi possível salvar agora. Tente novamente ou revise os dados informados."
        ),
      });
    } finally {
      setSavingVersion(false);
    }
  }

  async function handleDeleteVersion() {
    if (!selectedVersionId) {
      setVersionMessage({ severity: "warning", text: "Selecione uma versão para excluir." });
      return;
    }

    const confirmed = window.confirm("Deseja excluir esta simulação do histórico?");
    if (!confirmed) {
      return;
    }

    setSavingVersion(true);
    setVersionMessage(null);

    try {
      await excluirDreViabilidadeVersao(selectedVersionId);
      clearSelectedDreSimulation(
        tenant.clientId,
        selectedBackendEmpresaId,
        activeMonth
      );
      clearSelectedDreReference(
        tenant.clientId,
        selectedBackendEmpresaId
      );
      handleNewVersion();
      await refreshVersions("");
      setVersionMessage({ severity: "success", text: "Simulação excluída do histórico." });
    } catch (error) {
      logContabilidadeApiError("excluir-versao-dre", error);
      setVersionMessage({ severity: "error", text: "Não foi possível excluir a versão selecionada." });
    } finally {
      setSavingVersion(false);
    }
  }

  const isLoadingDre = (apiStatus === "idle" || apiStatus === "loading") && !usingApiData;
  const hasAnyData = usingApiData;
  const versionManagerPanel = (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Paper elevation={0} sx={{ borderRadius: 2.4, bgcolor: colors.surface, p: 2, border: `1px solid ${colors.border}` }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "flex-start", md: "center" }}>
            <Chip
              label={versionStatusLabel}
              size="small"
              sx={{ ...versionStatusColor, fontWeight: 800 }}
            />
            {selectedVersionTitle ? (
              <Typography sx={{ color: colors.muted, fontSize: 13 }}>
                Editando: <strong style={{ color: colors.text }}>{selectedVersionTitle}</strong>
              </Typography>
            ) : null}
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems={{ xs: "stretch", md: "center" }}>
            <TextField
              size="small"
              value={versionName}
              onChange={(event) => setVersionName(event.target.value)}
              placeholder="Nome da versão"
              disabled={!selectedBackendEmpreendimento}
              error={saveAttempted && !versionName.trim()}
              helperText={saveAttempted && !versionName.trim() ? "Nome obrigatorio" : ""}
              sx={{ minWidth: { xs: "100%", md: 240 }, flex: 1 }}
            />
            <TextField
              size="small"
              value={versionDescription}
              onChange={(event) => setVersionDescription(event.target.value)}
              placeholder="Descrição"
              disabled={!selectedBackendEmpreendimento}
              sx={{ minWidth: { xs: "100%", md: 260 }, flex: 1 }}
            />
          </Stack>
          <Divider />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
            <Button
              variant="outlined"
              startIcon={<AddRounded />}
              onClick={handleNewVersion}
              disabled={!selectedBackendEmpreendimento || savingVersion}
            >
              Nova versão
            </Button>
            <Button
              variant="contained"
              startIcon={savingVersion ? <CircularProgress size={16} color="inherit" /> : <SaveRounded />}
              onClick={handleSaveVersion}
              disabled={!canSaveVersion}
            >
              {savingVersion ? "Salvando..." : "Salvar"}
            </Button>
            {hasVersionHistory ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteRounded />}
                onClick={handleDeleteVersion}
                disabled={!selectedVersionId || savingVersion}
              >
                Excluir simulação selecionada
              </Button>
            ) : null}
            <Typography sx={{ color: colors.muted, fontSize: 13 }}>
              {saveHelperText}
            </Typography>
          </Stack>
          {selectedBackendEmpreendimento && versionsStatus === "success" && !hasVersionHistory ? (
            <Typography sx={{ color: colors.softMuted, fontSize: 12.5 }}>
              Nenhuma simulação salva para este empreendimento.
            </Typography>
          ) : null}
          {versionMessage ? (
            <Alert severity={versionMessage.severity} sx={{ py: 0.4 }}>
              {versionMessage.text}
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      {hasVersionHistory ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            overflow: "hidden",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={1.2}
            sx={{ px: 2, py: 1.6, borderBottom: `1px solid ${colors.border}` }}
          >
            <Box>
              <Typography sx={{ color: colors.text, fontWeight: 900 }}>
                Histórico de simulações
              </Typography>
              <Typography sx={{ color: colors.softMuted, fontSize: 12.5, mt: 0.35 }}>
                Versoes salvas para este empreendimento.
              </Typography>
            </Box>
            <Chip
              label={versionsStatus === "loading" ? "Atualizando" : `${versions.length} salva(s)`}
              size="small"
              sx={{ bgcolor: "#eef4ff", color: colors.primary, fontWeight: 800 }}
            />
          </Stack>

          <Stack spacing={1.2} sx={{ p: 2 }}>
            {versions.map((version) => {
              const selected = String(version.id) === String(selectedVersionId);
              const title = getVersionTitle(version);
              const description = version.descricao || version.descricaoVersao || "Sem descricao";
              const month = version.mesAnoBase || version.mes_ano_base || "Mês base não informado";

              return (
                <Stack
                  key={version.id}
                  role="button"
                  tabIndex={0}
                  direction="row"
                  spacing={1.4}
                  alignItems="flex-start"
                  onClick={() => handleVersionChange(String(version.id))}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleVersionChange(String(version.id));
                    }
                  }}
                  sx={{
                    p: 1.5,
                    borderRadius: 2.2,
                    border: `1px solid ${selected ? "#9bd8bf" : "#b7caff"}`,
                    bgcolor: selected ? "var(--ac-highlight-emerald, #f5fcf8)" : "var(--ac-highlight-blue, #f3f7ff)",
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                    outline: "none",
                    "&:hover": {
                      borderColor: selected ? "#08765c" : colors.primary,
                    },
                    "&:focus-visible": {
                      boxShadow: `0 0 0 3px rgba(37, 99, 235, 0.18)`,
                    },
                  }}
                >
                  <Box sx={{ pt: 0.15, color: selected ? "#08765c" : colors.primary }}>
                    <DescriptionOutlined fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ color: colors.text, fontWeight: 800, fontSize: 13.5 }}>
                      {title}
                    </Typography>
                    <Typography sx={{ color: colors.muted, fontSize: 12.5, mt: 0.35, lineHeight: 1.55 }}>
                      {description}
                    </Typography>
                    <Typography sx={{ color: colors.softMuted, fontSize: 12, mt: 0.35 }}>
                      Mês base: {month}
                    </Typography>
                  </Box>
                  <Chip
                    label={selected ? "Selecionada" : "Salva"}
                    size="small"
                    sx={{
                      alignSelf: "center",
                      bgcolor: selected ? "#d8f9e9" : "#eaf1ff",
                      color: selected ? "#08765c" : colors.primary,
                      fontWeight: 800,
                    }}
                  />
                </Stack>
              );
            })}
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );

  return (
    <AgenteContabilidadeShell
      title="DRE de Viabilidade"
      subtitle="Análise mensal comparativa do resultado do empreendimento"
      updating={apiStatus === "loading" && usingApiData}
      agenteContext={{
        paginaAtual: "dre-viabilidade",
        empresa: selectedBackendEmpreendimento,
        competencia: activeMonth,
        dadosVisiveis: {
          impostoPercentual,
          dadosCarregados,
          dre: calculatedRows,
          empresaSelecionada: selectedEmpreendimentoContext,
        },
      }}
      actions={[
        <FilterSelect
          key="empresa"
          value={selectedEmpreendimentoValue}
          onChange={handleEmpreendimentoChange}
          options={empreendimentoOptions}
          icon={BusinessRounded}
          minWidth={290}
        />,
      ]}
    >
      {isLoadingDre ? (
        <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
          <Stack alignItems="center" spacing={1.5} sx={{ textAlign: "center" }}>
            <CircularProgress size={34} />
            <Typography sx={{ fontWeight: 900, color: colors.text }}>
              Carregando dados da DRE
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 13 }}>
              Aguarde enquanto consultamos os dados carregados pela API.
            </Typography>
          </Stack>
        </Paper>
      ) : !hasAnyData ? (
        <EmptyState
          title="Nenhum dado disponível"
          description="Não encontramos dados disponíveis para exibição no momento."
        />
      ) : (
        <>
          {months.length ? (
            <>
              <PeriodSelector months={months} value={activeMonth} onChange={setSelectedMonth} />
              <MonthFallbackNotice requestedMonth={selectedMonth} activeMonth={activeMonth} />
            </>
          ) : null}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mb: 2 }}>
            <Typography sx={{ color: colors.muted, fontSize: 13 }}>
              Imposto informado: <strong style={{ color: colors.text }}>{formatPercent(impostoPercentual, { decimals: 2 })}</strong> do VGV Bruto
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 13 }}>
              Mês base dos dados: <strong style={{ color: colors.text }}>{activeMonth || "N/A"}</strong>
            </Typography>
          </Stack>
          <KpiGrid metrics={summaryMetrics} />
          <DreTable lineData={lineData} calculatedRows={calculatedRows} onCellChange={handleLineChange} />
          {versionManagerPanel}
        </>
      )}
    </AgenteContabilidadeShell>
  );
}
